import { useEffect, useMemo, useState } from 'react';
import SiteLayout, { Stripe } from './SiteLayout';
import { youtubeId, embedUrl } from './youtube';

// Every event video, newest first, grouped by year: the YouTube links added
// in Web Admin > Event videos (the same ones the Results Hub shows). Each
// card shows YouTube's picture for the video and swaps in YouTube's own
// player when pressed, so the page doesn't load a player per video. The
// intro and the links under it are edited in Web Admin > Videos page.

const DEFAULT_INTRO = 'Films of NHCA events, newest first. Press play to watch here, or open one on YouTube.';

function usePageFields() {
  const [fields, setFields] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/site/pages/videos')
      .then(res => (res.ok ? res.json() : null))
      .then(d => (d ? JSON.parse(d.body_md) : {}))
      .catch(() => ({}))
      .then(f => { if (!cancelled) setFields(f); });
    return () => { cancelled = true; };
  }, []);
  return fields;
}

function fmtDate(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' });
}

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  const id = youtubeId(video.url);
  if (!id) return null;
  return (
    <div className="site-album site-video-card">
      {playing ? (
        <span className="site-album-photo site-video-card-player">
          <iframe
            src={embedUrl(id, true)}
            title={video.title}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </span>
      ) : (
        <button type="button" className="site-album-photo site-video-card-play" onClick={() => setPlaying(true)}
          aria-label={`Play ${video.title}`}>
          <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" loading="lazy" />
          <span className="site-video-card-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </button>
      )}
      <span className="site-album-text">
        <span className="site-album-hill">{video.venue || video.hill}</span>
        <span className="site-album-meta">{fmtDate(video.date)}</span>
        <span className="site-album-desc">
          {video.title} · <a href={video.url} target="_blank" rel="noreferrer">YouTube</a>
        </span>
      </span>
    </div>
  );
}

export default function SiteVideos() {
  const [videos, setVideos] = useState(null);
  const [hill, setHill] = useState('');
  const fields = usePageFields();
  const pageLinks = (fields?.links || []).filter(l => l && l.label && l.href);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Videos · National Hill Climb Association';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/site/videos')
      .then(res => (res.ok ? res.json() : { videos: [] }))
      .catch(() => ({ videos: [] }))
      .then(d => { if (!cancelled) setVideos(d.videos || []); });
    return () => { cancelled = true; };
  }, []);

  const list = useMemo(() => videos || [], [videos]);
  const hills = useMemo(() => [...new Set(list.map(v => v.venue || v.hill))].sort(), [list]);
  const years = useMemo(() => {
    const byYear = new Map();
    for (const v of list) {
      if (hill && (v.venue || v.hill) !== hill) continue;
      if (!byYear.has(v.year)) byYear.set(v.year, []);
      byYear.get(v.year).push(v);
    }
    return [...byYear.entries()];
  }, [list, hill]);

  return (
    <SiteLayout>
      <section className="site-dark site-page-head">
        <div className="site-wrap site-section-head">
          <div>
            <span className="site-kicker">NHCA on film</span>
            <h1 className="site-h2">Videos</h1>
            <p>{fields ? (fields.intro || DEFAULT_INTRO) : ' '}</p>
            {pageLinks.length > 0 && (
              <div className="site-page-links">
                {pageLinks.map(l => (
                  <a key={l.href} href={l.href} target="_blank" rel="noreferrer">{l.label} →</a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Stripe />

      <section className="site-albums">
        <div className="site-wrap">
          {videos === null ? null : list.length === 0 ? (
            <p className="site-albums-empty">No videos yet. Check back soon.</p>
          ) : (
            <>
              <div className="site-albums-tools">
                <label>
                  <span>Hill</span>
                  <select value={hill} onChange={e => setHill(e.target.value)}>
                    <option value="">All hills</option>
                    {hills.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
                {years.length > 1 && (
                  <label>
                    <span>Year</span>
                    <select value="" onChange={e => document.getElementById(e.target.value)?.scrollIntoView()}>
                      <option value="">Jump to…</option>
                      {years.map(([y]) => <option key={y} value={`videos-${y}`}>{y}</option>)}
                    </select>
                  </label>
                )}
              </div>
              {years.map(([y, yearVideos]) => (
                <section key={y} id={`videos-${y}`} className="site-albums-year" aria-labelledby={`videos-${y}-title`}>
                  <h2 className="site-albums-year-title" id={`videos-${y}-title`}>{y}</h2>
                  <div className="site-album-grid">
                    {yearVideos.map(v => <VideoCard key={`${v.date}-${v.url}`} video={v} />)}
                  </div>
                </section>
              ))}
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
